import React, { useEffect, useRef } from 'react'
import { makeStyles } from '@mui/styles'

interface ToastProgressProps {
  duration?: number | null
  color: string
  paused?: boolean
  onComplete?: () => void
}

const useStyles = makeStyles(() => ({
  progress: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    display: 'block',
    width: '100%',
    height: 4,
    transform: 'scaleX(1)',
    transformOrigin: 'left',
    pointerEvents: 'none',
  },
}))

const ToastProgress = ({ duration, color, paused = false, onComplete }: ToastProgressProps) => {
  const progressRef = useRef<HTMLDivElement | null>(null)
  const animationRef = useRef<Animation | null>(null)
  const pausedRef = useRef(paused)
  const onCompleteRef = useRef<ToastProgressProps['onComplete']>(onComplete)
  const classes = useStyles()

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    pausedRef.current = paused

    const animation = animationRef.current

    if (!animation) return

    if (paused) {
      animation.pause()
    } else if (animation.playState === 'paused') {
      animation.play()
    }
  }, [paused])

  useEffect(() => {
    if (
      typeof duration !== 'number'
      || duration <= 0
      || !progressRef.current
    ) {
      return
    }

    const animation = progressRef.current.animate(
      [
        { transform: 'scaleX(1)' },
        { transform: 'scaleX(0)' },
      ],
      {
        duration,
        easing: 'linear',
        fill: 'forwards',
      },
    )

    animationRef.current = animation

    if (pausedRef.current) {
      animation.pause()
    }

    animation.onfinish = () => {
      onCompleteRef.current?.()
    }

    return () => {
      animation.onfinish = null
      animation.cancel()
      animationRef.current = null
    }
  }, [duration])

  if (typeof duration !== 'number' || duration <= 0) {
    return null
  }

  return (
    <div
      ref={progressRef}
      className={classes.progress}
      style={{ backgroundColor: color }}
    />
  )
}

export default ToastProgress
